import React, { useReducer, useState } from 'react';
import { Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Api from '../API';
import GenericTable from '../../Components/GenericTable/GenericTable';
import { InitialSearchesState, SearchesReducer } from '../reducer';
import { ICandidateSourcingSearch, ISearchesApiResponse } from '../types';
import SearchTableRows from '../SearchTableRows';
import { headerStyle } from '../../Candidates/config';
import { candidateSourcingActionsList } from '../config';
import CandidateSourcingTableActionComponents from '../CandidateSourcingTableActionComponents';
import SearchBar from '../SearchBar';
import GenericDialog from '../../Components/Modals/GenericDialog';

const Searches = () => {
  const [searchesState, dispatch] = useReducer(SearchesReducer, InitialSearchesState);
  const { searchesTableState, searchesTableSettings, searchesCount, totalPages, searchQuery } =
    searchesState;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState<ICandidateSourcingSearch | null>(null);

  const {
    data: searchesData,
    isLoading: isFetchingSearches,
    refetch: refetchSearches
  } = useQuery({
    queryKey: [
      'searches',
      searchesTableSettings.page,
      searchesTableSettings.limit,
      searchesTableSettings.sort,
      searchesTableSettings.sort_order,
      searchQuery
    ],
    queryFn: async () => {
      const response = await Api.getSearches(
        searchesTableSettings.page,
        searchesTableSettings.limit,
        searchesTableSettings.sort,
        searchesTableSettings.sort_order,
        searchQuery
      );
      const responseData = response as ISearchesApiResponse;

      if (responseData.resHead) {
        dispatch({
          type: 'SET_SEARCHES_COUNT',
          payload: parseInt(responseData.resHead['x-total-count'] || '0')
        });
        dispatch({
          type: 'SET_TOTAL_PAGES',
          payload: parseInt(responseData.resHead['x-total-pages'] || '0')
        });
      }

      return responseData;
    }
  });

  const handleSearchChange = (query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  };

  const handleSearch = () => {
    refetchSearches();
  };

  const handleClearSearch = () => {
    dispatch({ type: 'CLEAR_SEARCH_QUERY' });
  };

  const handleDeleteSearch = async () => {
    if (searchToDelete) {
      try {
        await Api.deleteSearch(searchToDelete.id);
        refetchSearches();
      } catch (error) {
        // Error handling for search deletion
      }
    }
    setDeleteDialogOpen(false);
    setSearchToDelete(null);
  };

  const handleDeleteClick = (search: ICandidateSourcingSearch) => {
    setSearchToDelete(search);
    setDeleteDialogOpen(true);
  };

  const searches = searchesData?.res?.searches || [];

  return (
    <Stack sx={{ padding: '20px' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: '20px' }}
      >
        <Stack direction="row" spacing={2} sx={{ flex: 1, marginRight: '20px' }}>
          <SearchBar
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            placeholder="Search by name"
          />
        </Stack>
      </Stack>

      <GenericTable
        label="searches list"
        items={searches as ICandidateSourcingSearch[]}
        isLoadingItems={isFetchingSearches}
        tableState={searchesTableState}
        dispatch={dispatch}
        headerStyle={headerStyle}
        paginationState={searchesTableSettings}
        pagination={{
          totalItems: searchesCount,
          totalPages
        }}
        TableRows={(props) => <SearchTableRows {...props} dispatch={dispatch} />}
        TableActionList={candidateSourcingActionsList}
        allowMultipleSelection={false}
        titleCellId="id"
        userPreferenceSource="searches"
      />

      <CandidateSourcingTableActionComponents
        actionsState={searchesTableState.actions}
        dispatch={dispatch}
        refetchSearches={refetchSearches}
        onDeleteClick={handleDeleteClick}
      />

      <GenericDialog
        isDialogOpen={deleteDialogOpen}
        setDialogOpen={setDeleteDialogOpen}
        title="Delete this search?"
        description="Are you sure you want to permanently delete this search? This action cannot be undone and all associated data will be deleted."
        buttonCallback={handleDeleteSearch}
        callbackLoading={false}
        buttonText="Delete"
        url=""
      />
    </Stack>
  );
};

export default Searches;
